import React, { useState, useEffect } from 'react';
import {
  TextField, Button, Box, Typography, Switch, FormControlLabel,
  InputLabel, FormControl, Select, MenuItem, Alert, CircularProgress
} from '@mui/material';
import { Layers } from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import { useDispatch } from 'react-redux';
import { createAdminCategory, updateAdminCategory } from '../redux/slices/catalogSlice';
import CatalogSeoFields from './CatalogSeoFields';

const CategoryModal = ({ open, onClose, category, onSuccess }) => {
  const dispatch = useDispatch();
  const isEdit = !!category;
  
  const [formData, setFormData] = useState({
    category_name: '',
    category_description: '',
    category_status: 'Activated',
    requires_sub_category_lead_match: false,
    image: '', seo_name: '', seo_description: '', description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (category && open) {
      setFormData({
        category_name: category.category_name || '',
        category_description: category.category_description || '',
        category_status: category.category_status || 'Activated',
        requires_sub_category_lead_match: !!category.requires_sub_category_lead_match,
        image: category.image || '',
        seo_name: category.seo_name || '',
        seo_description: category.seo_description || '',
        description: typeof category.description === 'string' ? category.description : '',
      });
    } else if (open) {
      setFormData({
        category_name: '', category_description: '', category_status: 'Activated',
        requires_sub_category_lead_match: false,
        image: '', seo_name: '', seo_description: '', description: '',
      });
    }
    setError('');
  }, [category, open]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const next = type === 'checkbox'
      ? checked
      : name === 'category_status' ? value : value.trimStart();
    setFormData({ ...formData, [name]: next });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_name.trim()) {
      setError('Category name is required');
      return;
    }
    
    setLoading(true);
    try {
      const trimmedData = {
        ...formData,
        category_name: formData.category_name.trim(),
        category_description: formData.category_description.trim(),
        image: formData.image.trim(),
        seo_name: formData.seo_name.trim(),
        seo_description: formData.seo_description.trim(),
        description: formData.description.trim(),
      };
      if (isEdit) {
        await dispatch(updateAdminCategory({ id: category._id, data: trimmedData })).unwrap();
      } else {
        await dispatch(createAdminCategory(trimmedData)).unwrap();
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Category' : 'Create New Category'}
        subtitle={isEdit ? 'Update main category attributes and routing options' : 'Add a primary catalog category'}
        icon={Layers}
        iconColor="primary"
        onClose={!loading ? onClose : undefined}
      />
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <AdminDialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
            <TextField
              label="Category Name"
              name="category_name"
              value={formData.category_name}
              onChange={handleChange}
              fullWidth
              required
              placeholder="e.g. Electronics, Fashion, Industrial"
            />
            
            <TextField
              label="Description (Optional)"
              name="category_description"
              value={formData.category_description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Brief summary describing products in this category..."
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="category_status"
                value={formData.category_status}
                label="Status"
                onChange={handleChange}
              >
                <MenuItem value="Activated">Activated</MenuItem>
                <MenuItem value="Deactivated">Deactivated</MenuItem>
              </Select>
            </FormControl>

            <CatalogSeoFields
              entityLabel="category"
              value={{
                image: formData.image,
                title: formData.seo_name,
                metaDescription: formData.seo_description,
                content: formData.description,
              }}
              onChange={(p) => setFormData((f) => ({
                ...f,
                ...(p.image !== undefined ? { image: p.image } : {}),
                ...(p.title !== undefined ? { seo_name: p.title } : {}),
                ...(p.metaDescription !== undefined ? { seo_description: p.metaDescription } : {}),
                ...(p.content !== undefined ? { description: p.content } : {}),
              }))}
            />

            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Switch
                    name="requires_sub_category_lead_match"
                    checked={!!formData.requires_sub_category_lead_match}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Route leads by sub-category</Typography>}
              />
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                {formData.requires_sub_category_lead_match
                  ? 'A lead in this category is only sent to sellers who list the exact sub-category the buyer asked for. Enquiries with no sub-category are held for your team instead of being sent out.'
                  : 'Leads are matched on this category, city and state only — the default, and right for any category where every seller can serve any enquiry in it.'}
              </Typography>
            </Box>
          </Box>
        </AdminDialogContent>
        
        <AdminDialogActions>
          <Button onClick={onClose} variant="outlined" disabled={loading} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Category'}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};

export default CategoryModal;

